import { useContext, useEffect, useState } from 'react';
import axios from '@/_utils/axios';
import { IngestFileTitleSerialized } from '@/redux/slices/slice_types';
import TitleTypeLabel from '@/components/_common/TitleTypeLabel';
import { Grid, Box, Typography } from '@mui/material';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { ConfigContext } from '@/contextAPI/configContext';

interface TitleAssociationDisplayProps {
  titleId: number | string | null;
  version?: IngestFileTitleSerialized;
  titleS?: IngestFileTitleSerialized;
}

const TitleAssociationDisplay = ({ titleId, version, titleS }: TitleAssociationDisplayProps) => {
  const [title, setTitle] = useState<IngestFileTitleSerialized | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { versionRender } = useContext(ConfigContext).ingestManagementConfig;

  useEffect(() => {
    setLoading(true);
    if (titleS) {
      setTitle(titleS);
      setLoading(false);
    } else if (!title) {
      axios
        .get(`inventory/titles/${titleId}/`)
        .then((res) => {
          setTitle(res.data.data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [titleId, titleS]);

  const getParentDataDisplay = () => {
    if (!title) return null;
    let titleDisplay = '';
    if (title.lineage_meta?.series_name) {
      titleDisplay += title.lineage_meta.series_name;
    }
    if (title.lineage_meta?.season_sequence_order) {
      titleDisplay += ` • S${title.lineage_meta.season_sequence_order}`;
    }
    if (title.lineage_meta?.episode_sequence_order) {
      titleDisplay += ` • E${title.lineage_meta.episode_sequence_order}`;
    }
    return titleDisplay;
  };

  const getTitleDataDisplay = () => {
    let display = title?.name || '';
    if (title?.entity_type === 'episode' || title?.entity_type === 'season') {
      display = `${title.entity_type.charAt(0).toUpperCase()}${Number(title.sequence_order)}: ${display}`;
    }
    return display;
  };

  if (loading) {
    return (
      <Box sx={{ color: 'primary.main', minHeight: 50 }}>
        <PolarisIcon icon="loading_dots" height={40} />
      </Box>
    );
  }

  return (
    <Grid container alignItems="center" justifyContent="space-between">
      <div>
        <Grid container alignItems="center" sx={{ minHeight: 50 }}>
          <Grid container alignItems="center" sx={{ marginRight: 1 }}>
            <TitleTypeLabel type={title?.entity_type || ''} small />
            <Typography variant="h6">{getTitleDataDisplay()}</Typography>
            {version ? (
              <Typography
                variant="caption"
                sx={{ ml: 1 }}
              >{` | ${typeof versionRender !== 'undefined' ? versionRender(version) : version.name}`}</Typography>
            ) : (
              ''
            )}
          </Grid>
          <Typography sx={{ fontSize: '1rem', opacity: 0.66, mt: 1 }}>{getParentDataDisplay()}</Typography>
        </Grid>
      </div>

      {/* our title data is too inconsistent to do this */}
      {/* if you ended up here because you were asked to add a link to the title page */}
      {/* ping Tanya  */}

      {/* <TitleRecordButton
        variant="text"
        color="default"
        href={buildUrl()}
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<PolarisIcon icon="external_link" style={{ color: 'var(--primary-main)' }} height={15} />}
        disabled={loading}
      >
        View Title Record
      </TitleRecordButton> */}
    </Grid>
  );
};

export default TitleAssociationDisplay;
